"use client";

import {
  HomeIcon,
  UserIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  HeartIcon,
  CalendarDaysIcon,
  CakeIcon,
  ClockIcon,
  TicketIcon,
  GiftIcon,
  RectangleStackIcon,
  IdentificationIcon,
  PhotoIcon,
  LifebuoyIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserRole } from "../../enums";

type NavLinkItem = {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  roles: UserRole[];
  children?: NavLinkItem[];
};

const links: NavLinkItem[] = [
  {
    name: "Home",
    href: "/client",
    icon: HomeIcon,
    roles: [UserRole.USER],
  },
  {
    name: "Home",
    href: "/admin",
    icon: HomeIcon,
    roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
  },
  
 
  {
    name: "Charities",
    href: "/admin/charities",
    icon: HeartIcon,
    roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
    children: [
      {
        name: "Charities",
        href: "/admin/charities",
        icon: HeartIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
      {
        name: "Charity Event Types",
        href: "/admin/charity-event-types",
        icon: TicketIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
      {
        name: "Charity Issue",
        href: "/admin/charity-issue-categories",
        icon: FlagIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
    ],
  },
  

  

  {
    name: "Greeting Occasions",
    href: "/admin/greeting-occasion-categories",
    icon: GiftIcon,
    roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
    children: [
      {
        name: "Greeting Memberships",
        href: "/admin/greeting-memberships",
        icon: IdentificationIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
      {
        name: "Occasion Collections",
        href: "/admin/occasion-collections",
        icon: RectangleStackIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
      {
        name: "Greeting Occasion",
        href: "/admin/greeting-occasion-categories",
        icon: GiftIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
      {
        name: "Greeting Occasion Images",
        href: "/admin/greeting-occasions-images",
        icon: PhotoIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
      {
        name: "Event Occasions",
        href: "/admin/event-occasions",
        icon: CalendarDaysIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
    ],
  },
  
  {
    name: "User Settings",
    href: "/admin/users",
    icon: UsersIcon,
    roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
    children: [
      {
        name: "Physical & Mental Symptoms",
        href: "/admin/physical-and-mental-symptoms",
        icon: LifebuoyIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
      {
        name: "Dietary Preferences",
        href: "/admin/dietary-preferences",
        icon: CakeIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
      {
        name: "Temporary Illnesses",
        href: "/admin/temporary-illnesses",
        icon: ClockIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
      {
        name: "Allergy",
        href: "/admin/allergy-categories",
        icon: ExclamationTriangleIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
    ]
  },
  {
    name: "Settings",
    href: "/admin/settings",
    icon: Cog6ToothIcon,
    roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
    children: [
      {
        name: "Terms and Conditions",
        href: "/admin/terms-and-conditions",
        icon: DocumentTextIcon,
        roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
      },
    ]
  },
 
  {
    name: "My Profile",
    href: "/admin/profile",
    icon: UserIcon,
    roles: [UserRole.ADMIN, UserRole.SUPERADMIN],
  },
  {
    name: "My Profile",
    href: "/client/profile",
    icon: UserIcon,
    roles: [UserRole.USER],
  },
];

function isLinkActive(href: string, pathName: string) {
  return pathName === href || pathName.startsWith(`${href}/`);
}

function filterLinksByRole(items: NavLinkItem[], role: UserRole): NavLinkItem[] {
  return items
    .filter((link) => !link.roles || link.roles.includes(role))
    .map((link) => ({
      ...link,
      children: link.children
        ? filterLinksByRole(link.children, role)
        : undefined,
    }))
    .filter((link) => !link.children || link.children.length > 0);
}

function linkClassName({
  active,
  nested = false,
}: {
  active: boolean;
  nested?: boolean;
}) {
  return clsx(
    "flex w-full items-center gap-2 rounded-md text-sm font-medium transition-colors",
    nested
      ? "h-10 px-2 py-2"
      : "h-[48px] grow justify-center p-3 md:flex-none md:justify-start md:p-2 md:px-3",
    active
      ? "bg-purple-100 text-purple-700 shadow-sm"
      : "bg-gray-50 text-gray-700 hover:bg-purple-50 hover:text-purple-600"
  );
}

function NavLeafLink({
  link,
  pathName,
  nested = false,
}: {
  link: NavLinkItem;
  pathName: string;
  nested?: boolean;
}) {
  const LinkIcon = link.icon;
  const active = isLinkActive(link.href, pathName);

  return (
    <Link
      to={link.href}
      className={linkClassName({ active, nested })}
      title={link.name}
    >
      <LinkIcon className={clsx(nested ? "h-4 w-4 shrink-0" : "h-6 w-6 shrink-0")} />
      <span className={clsx("truncate", nested ? "text-sm" : "hidden md:block")}>
        {link.name}
      </span>
      {nested && (
        <span
          className={clsx(
            "ml-auto hidden h-1.5 w-1.5 rounded-full md:block",
            active ? "bg-purple-600" : "bg-transparent"
          )}
        />
      )}
    </Link>
  );
}

function NavGroup({
  link,
  pathName,
}: {
  link: NavLinkItem;
  pathName: string;
}) {
  const LinkIcon = link.icon;
  const children = link.children ?? [];
  const isChildActive = children.some((child) =>
    isLinkActive(child.href, pathName)
  );
  const [expanded, setExpanded] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) {
      setExpanded(true);
    }
  }, [isChildActive, pathName]);

  const mobileHref = children[0]?.href ?? link.href;

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        className={clsx(
          linkClassName({ active: isChildActive }),
          "hidden w-full md:flex"
        )}
        aria-expanded={expanded}
      >
        <LinkIcon className="h-6 w-6 shrink-0" />
        <span className="truncate">{link.name}</span>
        <ChevronDownIcon
          className={clsx(
            "ml-auto h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200",
            expanded && "rotate-180 text-purple-600"
          )}
        />
      </button>

      <Link
        to={mobileHref}
        className={clsx(
          linkClassName({ active: isChildActive }),
          "md:hidden"
        )}
        title={link.name}
      >
        <LinkIcon className="h-6 w-6 shrink-0" />
      </Link>

      <div
        className={clsx(
          "hidden overflow-hidden transition-all duration-200 md:block",
          expanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="ml-3 space-y-1 border-l-2 border-purple-100 pl-2">
          {children.map((child) => (
            <NavLeafLink
              key={child.href}
              link={child}
              pathName={pathName}
              nested
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function NavItem({
  link,
  pathName,
}: {
  link: NavLinkItem;
  pathName: string;
}) {
  if (link.children?.length) {
    return <NavGroup link={link} pathName={pathName} />;
  }

  return <NavLeafLink link={link} pathName={pathName} />;
}

export default function NavLinks({ role = undefined }: { role?: UserRole }) {
  const location = useLocation();
  const pathName = location.pathname;

  const visibleLinks = useMemo(
    () => (role ? filterLinksByRole(links, role) : []),
    [role]
  );

  if (!role) return null;

  return (
    <nav className="flex grow flex-row gap-1 md:flex-col md:gap-2">
      {visibleLinks.map((link) => (
        <NavItem key={`${link.href}-${link.name}`} link={link} pathName={pathName} />
      ))}
    </nav>
  );
}
